import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Heart, Brain, Leaf, Users, Mail, Phone, MapPin, Star, Quote } from "lucide-react";
import { useState } from "react";
const Index = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log("Form submitted:", formData);
    alert("Thank you for your message! We'll get back to you soon.");
    setFormData({
      name: "",
      email: "",
      phone: "",
      message: ""
    });
  };
  const testimonials = [{
    name: "Sarah M.",
    condition: "IBS & Chronic Bloating",
    quote: "After years of struggling with IBS, Faraz's holistic approach finally gave me the answers I needed. His understanding of the mind-gut connection was life-changing.",
    rating: 5
  }, {
    name: "Michael R.",
    condition: "Food Sensitivities",
    quote: "Dr. Berjis helped me identify the root causes of my digestive issues. His functional medicine approach addressed what conventional doctors missed.",
    rating: 5
  }, {
    name: "Jennifer L.",
    condition: "Digestive Wellness",
    quote: "The personalized treatment plan and compassionate care I received transformed my relationship with food and my overall health.",
    rating: 5
  }];
  const faqs = [{
    question: "Who in Los Angeles can help me with my IBS naturally?",
    answer: "Faraz Berjis, a board-certified gastroenterologist turned functional digestive consultant, specializes in natural IBS treatment in Los Angeles. With over 20 years of medical experience, he combines conventional expertise with holistic healing approaches."
  }, {
    question: "What is Core Digestive Wellness?",
    answer: "Core Digestive Wellness is a functional digestive health practice that takes a holistic approach to healing. We focus on the mind-gut connection, using food as medicine, and addressing the root causes of digestive issues rather than just treating symptoms."
  }, {
    question: "Is this good for chronic bloating or food sensitivity?",
    answer: "Absolutely. Our functional medicine approach is particularly effective for chronic bloating and food sensitivities. We investigate the underlying causes and create personalized treatment plans that address your unique digestive challenges."
  }, {
    question: "What makes Faraz Berjis different from other health coaches?",
    answer: "Faraz is a board-certified gastroenterologist with over 20 years of medical experience who has transitioned to functional medicine. This unique combination of conventional medical expertise and holistic healing approaches sets him apart from typical health coaches."
  }, {
    question: "Can I work with Core Digestive Wellness virtually?",
    answer: "Yes! Core Digestive Wellness offers telehealth consultations, making Faraz Berjis's expertise accessible to individuals located anywhere, not just in Los Angeles."
  }];
  return <div className="min-h-screen bg-gradient-natural">
      {/* Hero Section */}
      <section className="relative py-20 px-4 text-center gradient-warm text-white">
        <div className="max-w-4xl mx-auto">
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Faraz Berjis: A Holistic Approach to 
            <span className="block text-accent-foreground/90">Digestive Wellness</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto leading-relaxed">
            Pioneering a new paradigm in health that connects mind, body, and spirit to uncover the root cause of digestive distress and empower genuine, lasting healing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-warm">
              <Mail className="mr-2 h-5 w-5" />
              Schedule Consultation
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Welcome Message */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-primary">
            Discover Your Unique Path to Health & Healing
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            In a world where the healthcare system often feels complex, fragmented, and impersonal, finding clarity and direction on your personal health journey can be a daunting task. Many individuals struggling with chronic digestive issues feel unheard, unseen, and uncertain about the path forward.
          </p>
          <div className="bg-accent/10 p-8 rounded-lg border border-accent/20">
            <p className="text-xl font-semibold text-primary mb-2">Welcome — you belong here.</p>
            <p className="text-muted-foreground">
              If you are seeking a compassionate guide, a confident direction, and a space where you are seen as a whole person, you have found the right place.
            </p>
          </div>
        </div>
      </section>

      {/* About Faraz Section */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                Meet Your Guide
              </Badge>
              
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                At the heart of Core Digestive Wellness is Faraz Berjis, a visionary practitioner whose journey has bridged the worlds of conventional medicine and holistic healing.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Heart className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-primary mb-1">20+ Years Experience</h3>
                    <p className="text-muted-foreground">Board-certified gastroenterologist with extensive clinical practice</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Brain className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Functional Medicine Pioneer</h3>
                    <p className="text-muted-foreground">Innovative approach addressing root causes, not just symptoms</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Leaf className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Holistic Healing</h3>
                    <p className="text-muted-foreground">Integrating mind, body, and spirit for complete wellness</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-accent p-8 rounded-lg shadow-natural">
              <Quote className="h-12 w-12 text-white mb-4" />
              <blockquote className="text-2xl font-semibold text-white mb-4">
                "True health is within us. Together we will find it."
              </blockquote>
              <cite className="text-white/80 font-medium">— FARAZ BERJIS</cite>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-primary">
              The Core Digestive Wellness Philosophy
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              More than a clinic, Core Digestive Wellness is a functional digestive health practice built on integrative and holistic principles, looking at the whole person to resolve root causes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <Brain className="h-12 w-12 text-accent mb-4" />
                <CardTitle className="text-primary">Functional Medicine</CardTitle>
                <CardDescription>Investigating the "Why"</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  A systems-biology approach focusing on identifying and addressing root causes. Instead of asking "What drug matches this disease?" we ask "Why has function been lost?"
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <Heart className="h-12 w-12 text-accent mb-4" />
                <CardTitle className="text-primary">Integrative Medicine</CardTitle>
                <CardDescription>Best of All Worlds</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Combining conventional medicine with evidence-based complementary therapies, integrating 20+ years of gastroenterology with natural healing modalities.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <Leaf className="h-12 w-12 text-accent mb-4" />
                <CardTitle className="text-primary">Mind-Gut Connection</CardTitle>
                <CardDescription>Holistic Healing</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Understanding the intricate relationship between mind, emotions, and digestive health through the gut-brain axis and food as medicine.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mind-Gut Connection Deep Dive */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-primary">
            The Mind-Gut Connection
          </h2>
          <div className="space-y-8">
            <Card className="shadow-natural">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-primary">
                  <Leaf className="h-6 w-6 text-accent" />
                  Food as Medicine: Nourishing the Gut-Brain Axis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  The "gut-brain axis" is the bidirectional communication network linking the brain's emotional centers with intestinal functions. Our approach uses "Food as Medicine" to directly influence this axis, calming inflammation and fostering a healthy microbiome, which in turn sends positive signals back to the brain.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-natural">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-primary">
                  <Brain className="h-6 w-6 text-accent" />
                  The Psychology of Eating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  To heal the gut, we must also address psychological factors. When stressed, our body enters "fight-or-flight" mode, diverting resources away from digestion. By exploring the psychology of eating, clients gain awareness of these patterns and build a healthier relationship with food.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-natural">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-primary">
                  <Heart className="h-6 w-6 text-accent" />
                  Mind-Body Tools for Deep Healing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  If stress can turn off digestion, then relaxation can turn it back on. Techniques like meditation and breathwork are powerful therapeutic tools that empower individuals to regulate their own physiology, shifting from a stressed state to a healing "rest-and-digest" state.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-primary">
            Who Can Benefit from This Approach?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <Users className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-primary">IBS & Digestive Issues</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  For those with IBS, persistent bloating, gas, and discomfort, our functional approach offers comprehensive investigation to find root causes.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <Leaf className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-primary">Natural Gut Healing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  For Los Angeles residents and beyond seeking natural digestive health management, guided by a board-certified gastroenterologist.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <Heart className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-primary">Personalized Health Plans</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Combining 20+ years of medical experience with functional principles to create holistic, medically-informed health plans.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center shadow-natural hover:shadow-warm transition-smooth">
              <CardHeader>
                <MapPin className="h-12 w-12 text-accent mx-auto mb-4" />
                <CardTitle className="text-primary">Telehealth Available</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Core Digestive Wellness offers virtual consultations, making expertise accessible to individuals located anywhere.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-primary">
            Patient Success Stories
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => <Card key={index} className="shadow-natural hover:shadow-warm transition-smooth">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-accent text-accent" />)}
                  </div>
                  <CardTitle className="text-primary">{testimonial.name}</CardTitle>
                  <CardDescription>{testimonial.condition}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Quote className="h-6 w-6 text-accent mb-2" />
                  <p className="text-muted-foreground italic">"{testimonial.quote}"</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-primary">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left text-primary hover:text-accent transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-primary">
              Let's Connect
            </h2>
            <p className="text-lg text-muted-foreground">
              Ready to begin your journey to digestive wellness? Reach out to schedule your consultation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold mb-6 text-primary">Get in Touch</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-accent" />
                  <span className="text-muted-foreground">North Hollywood, Los Angeles</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-accent" />
                  <span className="text-muted-foreground">Telehealth consultations available</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-accent" />
                  <span className="text-muted-foreground">Visit coredigestive.com for more information</span>
                </div>
              </div>
              
              <div className="mt-8">
                <h4 className="font-semibold mb-4 text-primary">Follow Us</h4>
                <div className="flex gap-4">
                  <Button variant="outline" size="sm" className="hover:bg-accent hover:text-white">
                    Facebook
                  </Button>
                  <Button variant="outline" size="sm" className="hover:bg-accent hover:text-white">
                    Instagram
                  </Button>
                  <Button variant="outline" size="sm" className="hover:bg-accent hover:text-white">
                    LinkedIn
                  </Button>
                </div>
              </div>
            </div>
            
            <Card className="shadow-natural">
              <CardHeader>
                <CardTitle className="text-primary">Send a Message</CardTitle>
                <CardDescription>We'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input id="name" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} required />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} required />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input id="phone" type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} />
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" rows={4} value={formData.message} onChange={e => setFormData({
                    ...formData,
                    message: e.target.value
                  })} placeholder="Tell us about your digestive health concerns..." required />
                  </div>
                  <Button type="submit" className="w-full gradient-warm text-white hover:opacity-90">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto text-center">
          <p className="mb-2">© 2024 Core Digestive Wellness. All rights reserved.</p>
          <p className="text-sm opacity-80">
            All information is based on content publicly available from coredigestive.com
          </p>
        </div>
      </footer>
    </div>;
};
export default Index;